import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { formatTooltipValue, getTooltipLabel, TooltipFieldConfig } from '../../utils/chart.utils';

export interface MultiSeriesScatterPoint {
  category: string;
  series: string;
  value: number;
  oem?: string;
  customer?: string;
  label?: string;
  [key: string]: unknown;
}

export interface OemBenchmarkBand {
  name: string;
  from: number;
  to: number;
  color?: string;
  opacity?: number;
  label?: string;
}

export interface ScatterAnnotation {
  name?: string;
  category?: string;
  value?: number;
  xAxis?: string | number;
  yAxis?: number;
  label?: string;
}

export interface ScatterLegendSymbolRow {
  key: string;
  symbol: string;
  symbolClass: string;
  color: string;
}

export interface ConfigurableMultiSeriesScatterData {
  categories?: string[];
  points?: MultiSeriesScatterPoint[];
  benchmarkBands?: OemBenchmarkBand[];
  annotations?: ScatterAnnotation[];
}

export interface ConfigurableMultiSeriesScatterChartConfig {
  categoryKey?: string;
  valueKey?: string;
  seriesKey?: string;
  oemKey?: string;
  customerKey?: string;
  labelKey?: string;
  pointsKey?: string;
  categoriesKey?: string;
  benchmarkBandsKey?: string;
  annotationsKey?: string;
  symbolMap?: Record<string, string>;
  colorMap?: Record<string, string>;
  colors?: string[];
  symbols?: string[];
  symbolSize?: number;
  symbolSizeMap?: Record<string, number>;
  blockSize?: string;
  showPointLabels?: boolean;
  pointLabelKey?: string;
  yAxis?: {
    name?: string;
    min?: number;
    max?: number;
    interval?: number;
    suffix?: string;
    decimals?: number;
  };
  yAxis2?: {
    name?: string;
    min?: number;
    max?: number;
    interval?: number;
  };
  xAxis?: {
    showVerticalLines?: boolean;
    verticalLineColor?: string;
    verticalLineType?: 'solid' | 'dashed' | 'dotted';
    axisLabel?: {
      width?: number;
      overflow?: 'truncate' | 'break' | 'breakAll';
      interval?: number;
      hideOverlap?: boolean;
      lineHeight?: number;
      margin?: number;
    };
  };
  tooltip?: {
    autoCapitalizeKeys?: boolean;
    fields?: TooltipFieldConfig[];
  };
}

export const DEFAULT_SCATTER_COLORS = [
  '#118DFF',
  '#1AAB40',
  '#DA291C',
  '#5C2D91',
  '#E66C37',
  '#6B007B',
  '#D64550',
  '#2D8C7F',
];

export const DEFAULT_SCATTER_SYMBOLS = [
  'circle',
  'rect',
  'triangle',
  'diamond',
  'pin',
  'arrow',
  'roundRect',
  'path://M0,-8L7,-3L4,7L-4,7L-7,-3Z',
  'path://M0,-9L2,-2L9,-2L3,2L5,9L0,4L-5,9L-3,2L-9,-2L-2,-2Z',
];

@Component({
  selector: 'app-configurable-multi-series-scatter-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './configurable-multi-series-scatter-chart.component.html',
  styleUrl: './configurable-multi-series-scatter-chart.component.scss',
})
export class ConfigurableMultiSeriesScatterChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];
  legendSymbolRows: ScatterLegendSymbolRow[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private reflowSub?: Subscription;

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
    });

    requestAnimationFrame(() => this.loadAndRenderChart(initialFilters ?? undefined));

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;
    this.ensureChartContainerSize(chartDom);

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        this.renderChart(res?.data ?? res);
        this.addResizeListener();
      },
      error: () => {
        this.myChart?.hideLoading();
        this.renderChart();
      },
    });
  }

  protected renderChart(rawData?: ConfigurableMultiSeriesScatterData | Record<string, unknown>): void {
    const chartConfig = this.getChartConfig();
    const normalized = normalizeMultiSeriesScatterData(rawData, chartConfig);
    const seriesNames = getUniqueSeriesNames(normalized.points);
    const categories = normalized.categories.length
      ? normalized.categories
      : getUniqueCategories(normalized.points);

    this.allSeriesNames = seriesNames;
    this.allCategories = categories;

    const legendSymbolRows: ScatterLegendSymbolRow[] = [];
    const series = seriesNames.map((seriesName, index) => {
      const resolvedColor = this.wrapper.applyColorConsistency(
        seriesName,
        resolveScatterColor(seriesName, index, chartConfig),
      ) || resolveScatterColor(seriesName, index, chartConfig);
      const symbol = resolveScatterSymbol(seriesName, index, chartConfig);
      legendSymbolRows.push({
        key: seriesName,
        symbol,
        symbolClass: resolveLegendSymbolClass(symbol, index),
        color: resolvedColor,
      });
      const points = normalized.points
        .filter((point) => point.series === seriesName)
        .map((point) => ({
          name: point.category,
          ...point,
          rawValue: point.value,
          value: [point.category, point.value],
          itemStyle: {
            color: resolvedColor,
          },
        }));

      return {
        name: seriesName,
        type: 'scatter',
        symbol,
        symbolSize: resolveScatterSymbolSize(symbol, chartConfig),
        data: points,
        emphasis: {
          focus: 'series',
        },
        label: this.buildPointLabelOption(chartConfig),
        itemStyle: {
          color: resolvedColor,
        },
        ...(index === 0
          ? {
              markArea: this.buildBenchmarkMarkArea(normalized.benchmarkBands),
              markPoint: this.buildAnnotationMarkPoints(normalized.annotations),
            }
          : {}),
      };
    });
    this.legendSymbolRows = legendSymbolRows;

    const option = {
      color: seriesNames.map((name, index) => resolveScatterColor(name, index, chartConfig)),
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => this.buildTooltip(params, chartConfig),
      },
      legend: {
        show: false,
        data: seriesNames,
      },
      grid: {
        left: '1%',
        right: normalized.benchmarkBands.length ? '5%' : '1%',
        top: this.resolveTopGridSpacing(chartConfig, categories),
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        position: 'top',
        data: categories,
        triggerEvent: true,
        axisTick: { alignWithLabel: true },
        axisLabel: {
          width: chartConfig.xAxis?.axisLabel?.width ?? 90,
          interval: chartConfig.xAxis?.axisLabel?.interval ?? 0,
          overflow: chartConfig.xAxis?.axisLabel?.overflow ?? 'breakAll',
          hideOverlap: chartConfig.xAxis?.axisLabel?.hideOverlap ?? false,
          lineHeight: chartConfig.xAxis?.axisLabel?.lineHeight ?? 14,
          margin: chartConfig.xAxis?.axisLabel?.margin ?? 12,
        },
        splitLine: this.buildXAxisVerticalLines(chartConfig),
      },
      yAxis: [
        this.buildPrimaryYAxis(chartConfig),
        this.buildBenchmarkYAxis(chartConfig, normalized.benchmarkBands),
      ],
      series,
    };

    this.myChart.setOption(option, true);
    setTimeout(() => {
      this.myChart?.hideLoading();
      this.myChart?.resize();
    }, 0);
    this.wrapper.afterRender();
  }

  protected ensureChartContainerSize(chartDom: HTMLElement): void {
    const configuredBlockSize = this.data?.chartConfig?.blockSize || this.data?.blockSize;
    const configuredHeight = this.data?.height;

    if (configuredBlockSize) {
      chartDom.style.blockSize = configuredBlockSize;
      // also set inline height so global CSS !important rules don't override widget size
      chartDom.style.setProperty('height', String(configuredBlockSize), 'important');
    }

    if (configuredHeight) {
      // prefer explicit `height` key when provided in widget JSON
      chartDom.style.setProperty('height', String(configuredHeight), 'important');
    }
  }

  private getChartConfig(): ConfigurableMultiSeriesScatterChartConfig {
    return this.data?.chartConfig ?? {};
  }

  protected buildXAxisVerticalLines(config: ConfigurableMultiSeriesScatterChartConfig): any {
    const xAxisConfig = config.xAxis ?? {};

    // By default do not render vertical grid lines below the chart.
    // Only show them when explicitly enabled via `xAxis.showVerticalLines: true` in config.
    const showVerticalLines = xAxisConfig.showVerticalLines === false;

    return {
      show: showVerticalLines,
      lineStyle: {
        color: xAxisConfig.verticalLineColor ?? '#d8dee4',
        type: xAxisConfig.verticalLineType ?? 'solid',
      },
    };
  }

  private resolveTopGridSpacing(
    config: ConfigurableMultiSeriesScatterChartConfig,
    categories: string[],
  ): number {
    const labelCfg = config.xAxis?.axisLabel;
    const labelWidth = Math.max(40, labelCfg?.width ?? 90);
    const estimatedCharsPerLine = Math.max(4, Math.floor(labelWidth / 7));
    const longestCategoryLength = categories.reduce(
      (max, category) => Math.max(max, String(category || '').length),
      0,
    );
    const estimatedLines = Math.max(1, Math.ceil(longestCategoryLength / estimatedCharsPerLine));
    const lineHeight = labelCfg?.lineHeight ?? 14;
    const margin = labelCfg?.margin ?? 12;

    // x-axis labels are rendered on top; increase top grid so wrapped labels are never clipped.
    return 14 + estimatedLines * lineHeight + margin;
  }

  private buildPrimaryYAxis(config: ConfigurableMultiSeriesScatterChartConfig): any {
    const axisCfg = config.yAxis ?? {};
    const suffix = axisCfg.suffix ?? '%';
    const decimals = axisCfg.decimals ?? 0;

    return {
      type: 'value',
      min: axisCfg.min,
      max: axisCfg.max,
      interval: axisCfg.interval ?? 5,
      name: axisCfg.name,
      nameLocation: 'center',
      nameGap: 42,
      axisLabel: {
        formatter: (value: number) => formatPercentAxisValue(value, suffix, decimals),
      },
      splitLine: {
        show: false,
        lineStyle: { type: 'dashed' },
      },
      triggerEvent: true,
    };
  }

  protected buildBenchmarkYAxis(
    config: ConfigurableMultiSeriesScatterChartConfig,
    bands: OemBenchmarkBand[],
  ): any {
    const axisCfg = config.yAxis2 ?? {};
    const scale = this.resolveBenchmarkAxisScale(config, bands);

    return {
      type: 'value',
      position: 'right',
      min: scale.min,
      max: scale.max,
      interval: scale.interval,
      name: axisCfg.name,
      nameLocation: 'center',
      nameGap: 34,
      axisLabel: {
        inside: false,
        margin: 4,
        align: 'left',
        formatter: (value: number) => this.resolveBandLabel(value, bands),
      },
      axisTick: { show: false },
      splitLine: { show: false },
      triggerEvent: true,
    };
  }

  private resolveBenchmarkAxisScale(
    config: ConfigurableMultiSeriesScatterChartConfig,
    bands: OemBenchmarkBand[],
  ): { min: number | undefined; max: number | undefined; interval: number | undefined } {
    const configuredMin = config.yAxis2?.min ?? config.yAxis?.min;
    const configuredMax = config.yAxis2?.max ?? config.yAxis?.max;
    const configuredInterval = config.yAxis2?.interval ?? config.yAxis?.interval;

    if (!bands.length) {
      return {
        min: configuredMin,
        max: configuredMax,
        interval: configuredInterval,
      };
    }

    const bandStarts = bands.map((band) => Number(band.from)).filter(Number.isFinite);
    const bandEnds = bands.map((band) => Number(band.to)).filter(Number.isFinite);
    const spans = bands
      .map((band) => Number(band.to) - Number(band.from))
      .filter((span) => Number.isFinite(span) && span > 0);

    const inferredMin = bandStarts.length ? Math.min(...bandStarts) : configuredMin;
    const inferredMax = bandEnds.length ? Math.max(...bandEnds) : configuredMax;
    const inferredInterval = spans.length ? Math.min(...spans) : configuredInterval;

    return {
      min: configuredMin ?? inferredMin,
      max: configuredMax ?? inferredMax,
      interval: configuredInterval ?? inferredInterval,
    };
  }

  private buildBenchmarkMarkArea(bands: OemBenchmarkBand[]): any {
    if (!bands.length) {
      return undefined;
    }

    return {
      silent: true,
      data: bands.map((band) => [
        {
          yAxis: band.from,
          itemStyle: {
            color: band.color ?? '#d9e8ff',
            opacity: band.opacity ?? 0.18,
          },
          label: {
            show: false,
            position: 'insideRight',
            formatter: band.label || band.name,
            color: '#3d464d',
          },
        },
        {
          yAxis: band.to,
        },
      ]),
    };
  }

  protected buildAnnotationMarkPoints(annotations: ScatterAnnotation[]): any {
    if (!annotations.length) {
      return undefined;
    }

    return {
      symbol: 'pin',
      symbolSize: 44,
      data: annotations.map((annotation) => ({
        name: annotation.name,
        xAxis: annotation.xAxis ?? annotation.category,
        yAxis: annotation.yAxis ?? annotation.value,
        label: {
          formatter: annotation.label ?? annotation.name,
        },
      })),
    };
  }

  protected buildPointLabelOption(config: ConfigurableMultiSeriesScatterChartConfig): any {
    if (!config.showPointLabels) {
      return { show: false };
    }

    const labelKey = config.pointLabelKey || config.labelKey;
    return {
      show: true,
      position: 'top',
      formatter: (params: any) => {
        const point = params.data || {};
        return labelKey ? point[labelKey] || '' : point.label || point.series || '';
      },
    };
  }

  protected buildTooltip(params: any, config: ConfigurableMultiSeriesScatterChartConfig): string {
    const point = params.data || {};
    const tooltipCfg = config.tooltip;
    const fields = tooltipCfg?.fields?.length
      ? tooltipCfg.fields
      : [
          { key: '_category', label: 'Category' },
          { key: '_series', label: 'Series' },
          { key: '_value', label: 'Value', suffix: config.yAxis?.suffix ?? '%' },
        ];
    const autoCapitalize = tooltipCfg?.autoCapitalizeKeys !== false;
    const lines = [`${params.marker || ''}<b>${params.seriesName}</b>`];

    fields.forEach((field) => {
      const label = getTooltipLabel(field, autoCapitalize);
      const rawValue = resolveTooltipFieldValue(point, params, field.key);
      lines.push(`<div><b>${label}:</b> ${formatTooltipValue(rawValue, field)}</div>`);
    });

    return `<div>${lines.join('')}</div>`;
  }

  private resolveBandLabel(value: number, bands: OemBenchmarkBand[]): string {
    const matchingBand = bands.find((band) => nearlyEqual(value, band.from));
    if (matchingBand) {
      const name = matchingBand.label || matchingBand.name;
      return name || '';
    }
    return '';
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}

export function normalizeMultiSeriesScatterData(
  rawData: any,
  config: ConfigurableMultiSeriesScatterChartConfig,
): Required<ConfigurableMultiSeriesScatterData> {
  const pointsKey = config.pointsKey || 'points';
  const categoriesKey = config.categoriesKey || 'categories';
  const benchmarkBandsKey = config.benchmarkBandsKey || 'benchmarkBands';
  const annotationsKey = config.annotationsKey || 'annotations';
  const rawPoints = Array.isArray(rawData?.[pointsKey]) ? rawData[pointsKey] : [];

  return {
    categories: Array.isArray(rawData?.[categoriesKey]) ? rawData[categoriesKey].map(String) : [],
    points: rawPoints.map((point: any) => normalizePoint(point, config)).filter((point: MultiSeriesScatterPoint | null) => point !== null),
    benchmarkBands: normalizeBands(rawData?.[benchmarkBandsKey]),
    annotations: Array.isArray(rawData?.[annotationsKey]) ? rawData[annotationsKey] : [],
  };
}

export function normalizePoint(
  point: any,
  config: ConfigurableMultiSeriesScatterChartConfig,
): MultiSeriesScatterPoint | null {
  const categoryKey = config.categoryKey || 'category';
  const valueKey = config.valueKey || 'value';
  const seriesKey = config.seriesKey || 'series';
  const oemKey = config.oemKey || 'oem';
  const customerKey = config.customerKey || 'customer';
  const labelKey = config.labelKey || 'label';
  const category = point?.[categoryKey];
  const value = Number(point?.[valueKey]);
  const series = point?.[seriesKey] ?? point?.[customerKey] ?? point?.[oemKey];

  if (category === undefined || category === null || !Number.isFinite(value) || !series) {
    return null;
  }

  return {
    ...point,
    category: String(category),
    value,
    series: String(series),
    oem: point?.[oemKey] !== undefined ? String(point[oemKey]) : undefined,
    customer: point?.[customerKey] !== undefined ? String(point[customerKey]) : undefined,
    label: point?.[labelKey] !== undefined ? String(point[labelKey]) : undefined,
  };
}

export function normalizeBands(rawBands: any): OemBenchmarkBand[] {
  if (!Array.isArray(rawBands)) {
    return [];
  }

  return rawBands
    .map((band) => ({
      name: String(band?.name ?? band?.label ?? ''),
      from: Number(band?.from),
      to: Number(band?.to),
      color: band?.color,
      opacity: Number.isFinite(Number(band?.opacity)) ? Number(band.opacity) : undefined,
      label: band?.label,
    }))
    .filter((band) => band.name && Number.isFinite(band.from) && Number.isFinite(band.to));
}

export function getUniqueSeriesNames(points: MultiSeriesScatterPoint[]): string[] {
  return Array.from(new Set(points.map((point) => point.series).filter(Boolean)));
}

export function getUniqueCategories(points: MultiSeriesScatterPoint[]): string[] {
  return Array.from(new Set(points.map((point) => point.category).filter(Boolean)));
}

export function resolveScatterColor(
  seriesName: string,
  index: number,
  config: ConfigurableMultiSeriesScatterChartConfig,
): string {
  const palette = config.colors?.length ? config.colors : DEFAULT_SCATTER_COLORS;
  return config.colorMap?.[seriesName] || palette[index % palette.length];
}

export function resolveScatterSymbol(
  seriesName: string,
  index: number,
  config: ConfigurableMultiSeriesScatterChartConfig,
): string {
  const symbols = config.symbols?.length ? config.symbols : DEFAULT_SCATTER_SYMBOLS;
  return config.symbolMap?.[seriesName] || symbols[index % symbols.length];
}

export function resolveScatterSymbolSize(
  symbol: string,
  config: ConfigurableMultiSeriesScatterChartConfig,
): number {
  const baseSize = Number(config.symbolSize ?? 12);
  const safeBaseSize = Number.isFinite(baseSize) && baseSize > 0 ? baseSize : 12;
  const configuredSize = Number(config.symbolSizeMap?.[symbol]);
  if (Number.isFinite(configuredSize) && configuredSize > 0) {
    return configuredSize;
  }

  // ECharts' built-in pin draws the rounded head at 3/5 of the symbol box.
  // Scale the box so the visible pin head matches the configured point size.
  return symbol === 'pin' ? Math.round(safeBaseSize * 5 / 3) : safeBaseSize;
}

export function resolveLegendSymbolClass(symbol: string, index: number): string {
  if (symbol.startsWith('path://')) {
    return index % 2 === 0 ? 'custom-a' : 'custom-b';
  }

  return symbol;
}

export function formatPercentAxisValue(value: number, suffix = '%', decimals = 0): string {
  const rounded = decimals > 0 ? Number(value).toFixed(decimals) : String(Math.round(Number(value)));
  return `${rounded}${suffix}`;
}

export function resolveTooltipFieldValue(point: any, params: any, key: string): unknown {
  if (key === '_category') return point.category ?? params.name;
  if (key === '_series') return point.series ?? params.seriesName;
  if (key === '_value' || key === 'value') return point.rawValue ?? point.value?.[1] ?? point.value;
  if (key === '_oem') return point.oem;
  if (key === '_customer') return point.customer;
  return point[key];
}

function nearlyEqual(a: number, b: number, epsilon = 0.0001): boolean {
  return Math.abs(Number(a) - Number(b)) <= epsilon;
}
